import React, { useState, useEffect, useRef } from 'react';

interface GameBackdropProps {
  backdropUrl?: string | null;
  gameName?: string;
  className?: string;
}

const TRANSITION_MS = 2000; // 2s

// Composant pour l'ancienne image qui fait le fade-out
const PreviousBackdrop: React.FC<{ backdropUrl: string; transitionMs: number }> = ({ 
  backdropUrl, 
  transitionMs 
}) => {
  const [opacity, setOpacity] = useState(1);

  useEffect(() => {
    // Lance le fade-out après le mount
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setOpacity(0);
      });
    });
  }, []);

  return (
    <div
      className="absolute inset-0"
      style={{
        opacity: opacity,
        transition: `opacity ${transitionMs}ms ease-in-out`,
        zIndex: 1,
      }}
      aria-hidden
    >
      {/* Image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(${backdropUrl})`,
        }}
      />
      {/* Overlays de teinte identiques */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900/85 via-slate-950/90 to-[#0a0f1c]/90" />
      <div className="absolute inset-0 bg-gradient-to-t from-blue-600/10 via-transparent to-transparent" />
      <div className="absolute inset-0 shadow-[inset_0_0_180px_rgba(12,15,28,0.80)]" />
    </div>
  );
};

export const GameBackdrop: React.FC<GameBackdropProps> = ({
  backdropUrl,
  gameName,
  className = ''
}) => {
  const [currentBackdrop, setCurrentBackdrop] = useState(backdropUrl || null);
  const [previousBackdrop, setPreviousBackdrop] = useState<string | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [opacity, setOpacity] = useState(0);
  const [transitionKey, setTransitionKey] = useState(0);
  const timeoutRef = useRef<number | null>(null);

  // Apparition initiale du backdrop courant (0 -> 1)
  useEffect(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setOpacity(1);
      });
    });
    return () => {
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, []);

  useEffect(() => {
    // Gestion robuste : null, undefined, ou chaîne vide → null (pas de backdrop)
    const newBackdrop = (backdropUrl && backdropUrl.trim()) ? backdropUrl : null;
    
    // Si pas de changement, on ne fait rien
    if (newBackdrop === currentBackdrop) return;

    // Si pas de backdrop, on le définit directement
    if (!newBackdrop) {
      setPreviousBackdrop(currentBackdrop);
      setCurrentBackdrop(null);
      setIsTransitioning(true);
      setTransitionKey(prev => prev + 1);
      
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
      timeoutRef.current = window.setTimeout(() => {
        setIsTransitioning(false);
        setPreviousBackdrop(null);
      }, TRANSITION_MS + 50);
      return;
    }

    // Précharger la nouvelle image avant de lancer la transition
    const img = new Image();
    img.src = newBackdrop;
    img.onload = () => {
      // Empile: ancienne image en "previous" AU-DESSUS, nouvelle en dessous
      setPreviousBackdrop(currentBackdrop);
      setCurrentBackdrop(newBackdrop);
      setIsTransitioning(true);
      setTransitionKey(prev => prev + 1); // Clé unique pour forcer la recréation

      // Nettoyage à la fin de la transition
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
      timeoutRef.current = window.setTimeout(() => {
        setIsTransitioning(false);
        setPreviousBackdrop(null);
      }, TRANSITION_MS + 50);
    };
    
    img.onerror = () => {
      setCurrentBackdrop(null);
      setIsTransitioning(false);
    };
  }, [backdropUrl, currentBackdrop]);

  return (
    <div className={`w-full h-full overflow-hidden ${className}`}>
      {/* Ancienne image (AU-DESSUS, fade-out 1 -> 0) avec clé unique */}
      {previousBackdrop && (
        <PreviousBackdrop
          key={`prev-${transitionKey}`}
          backdropUrl={previousBackdrop}
          transitionMs={TRANSITION_MS}
        />
      )}
      
      {/* Image actuelle (dessous, visible) - seulement si un backdrop existe */}
      {currentBackdrop && (
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${currentBackdrop})`,
            opacity: opacity,
            transition: `opacity ${TRANSITION_MS}ms ease-in-out`,
            zIndex: 0,
          }}
          role="img"
          aria-label={gameName ? `Backdrop ${gameName}` : 'Backdrop'}
        />
      )}
      
      {/* Overlays — teintes strictement inchangées */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900/85 via-slate-950/90 to-[#0a0f1c]/90" />
      <div className="absolute inset-0 bg-gradient-to-t from-blue-600/10 via-transparent to-transparent" />
      <div className="absolute inset-0 shadow-[inset_0_0_180px_rgba(12,15,28,0.80)]" />
    </div>
  );
};